<script setup lang="ts">
/**
 * Settings sheet — brief 11-layout2 §1: reachable from the top bar's
 * overflow menu. Currently just the one setting the brief calls for: which
 * edge the tool strip docks to in landscape (Tessellation/Layers tab access
 * always takes the opposite edge, swapping together, never independently).
 */
import { IconLayoutSidebarLeftExpand, IconLayoutSidebarRightExpand, IconX } from '@tabler/icons-vue';
import { useUiStore } from '@/stores/ui';

const ui = useUiStore();

function close(): void {
  ui.showSettings = false;
}
</script>

<template>
  <div v-if="ui.showSettings" class="sheet-backdrop" @click.self="close">
    <div class="sheet" role="dialog" aria-modal="true" aria-label="Settings">
      <header>
        <h2>Settings</h2>
        <button class="icon-close" aria-label="Close" @click="close">
          <IconX :size="18" stroke-width="2" />
        </button>
      </header>

      <section>
        <h3>Tool strip side</h3>
        <p class="lead">
          In landscape, the tool strip docks to one edge and the Tessellation/Layers
          tabs dock to the other. Choose which side the tools sit on.
        </p>
        <div class="seg">
          <button
            class="seg-btn"
            :class="{ active: ui.dominantSide === 'left' }"
            @click="ui.dominantSide = 'left'"
          >
            <IconLayoutSidebarLeftExpand :size="16" stroke-width="1.75" />
            <span>Left</span>
          </button>
          <button
            class="seg-btn"
            :class="{ active: ui.dominantSide === 'right' }"
            @click="ui.dominantSide = 'right'"
          >
            <IconLayoutSidebarRightExpand :size="16" stroke-width="1.75" />
            <span>Right</span>
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.sheet-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(8, 8, 16, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: max(16px, var(--safe-top)) 16px max(16px, var(--safe-bottom));
}

.sheet {
  width: min(400px, 100%);
  max-height: 100%;
  overflow-y: auto;
  background: var(--ui-surface);
  border: 1px solid var(--ui-border);
  border-radius: 12px;
  padding: 20px 22px;
}

header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

h2 {
  margin: 0;
  font-size: 18px;
  color: var(--text-primary);
}

.icon-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--text-muted);
  cursor: pointer;
}

.icon-close:hover {
  color: var(--text-primary);
}

h3 {
  margin: 0 0 6px;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--text-muted);
}

.lead {
  margin: 0 0 12px;
  font-size: 13px;
  line-height: 1.6;
  color: var(--text-muted);
}

.seg {
  display: flex;
  gap: 2px;
  padding: 2px;
  background: color-mix(in srgb, var(--ui-border) 35%, transparent);
  border: 1px solid var(--ui-border);
  border-radius: 9px;
  width: fit-content;
}

.seg-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 34px;
  padding: 6px 14px;
  font-size: 13px;
  background: transparent;
  color: var(--text-primary);
  border: none;
  border-radius: 7px;
  cursor: pointer;
}

.seg-btn.active {
  background: color-mix(in srgb, var(--accent) 22%, transparent);
}
</style>
